import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import i18n from "i18next";
import { PageHeader } from "./Dashboard";
import { useTranslation } from "react-i18next";
import { Scale, AlertTriangle, Check } from "lucide-react";

export default function Bias() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/bias/report").then((r) => setData(r.data)); }, []);

    if (!data) return <PageHeader title={t("pages.bias_title")}><div className="text-[var(--jr-text-2)]">{t("common.loading")}</div></PageHeader>;
    const m = data.metrics;
    const passes = m.four_fifths_rule_pass;

    return (
        <PageHeader title={t("pages.bias_title")} subtitle={t("pages.bias_subtitle")}>
            <div className="grid lg:grid-cols-[1fr_320px] gap-6">
                <div className="space-y-4">
                    <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-6">
                        <h3 className="font-display text-lg tracking-tight mb-4">Gender-distributie (heuristic)</h3>
                        <div className="grid grid-cols-3 gap-3">
                            {["f", "m", "u"].map((g) => {
                                const item = m.gender_distribution[g];
                                const label = g === "f" ? "F (geschat)" : g === "m" ? "M (geschat)" : i18n.t("companies.unknown");
                                return (
                                    <div key={g} className="rounded-lg border border-white/10 p-4 bg-[var(--jr-surface-2)]">
                                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
                                        <div className="font-display text-3xl tracking-tighter mt-2">{item.pct}%</div>
                                        <div className="text-xs text-[var(--jr-text-2)] mt-1">{item.count} kandidaten</div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-xs text-[var(--jr-text-3)] mt-4 italic">⚠ Productie gebruikt voluntary self-id, niet name-heuristics.</p>
                    </div>

                    <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-6">
                        <h3 className="font-display text-lg tracking-tight mb-4">Goedkeurings-bias (4/5-regel)</h3>
                        <div className="space-y-2">
                            {Object.entries(m.approval_rates_by_gender_pct).map(([g, r]) => (
                                <Bar key={g} label={g === "f" ? "F" : g === "m" ? "M" : i18n.t("companies.unknown")} value={r}/>
                            ))}
                        </div>
                        <div className={`mt-5 rounded-lg p-4 flex items-start gap-3 ${passes ? "bg-[var(--jr-success)]/10 border border-[var(--jr-success)]/30" : "bg-[var(--jr-amber)]/10 border border-[var(--jr-amber)]/30"}`}>
                            {passes ? <Check className="text-[var(--jr-success)] mt-0.5" size={18}/> : <AlertTriangle className="text-[var(--jr-amber)] mt-0.5" size={18}/>}
                            <div>
                                <div className="text-sm font-medium text-white">{passes ? i18n.t("bias.rule_pass") : i18n.t("bias.rule_attention")}</div>
                                <p className="text-xs text-[var(--jr-text-2)] mt-1">{i18n.t("bias.rule_explain")}</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <DistCard title="Seniority" items={m.seniority_distribution}/>
                        <DistCard title="Locatie (top 8)" items={m.location_distribution}/>
                    </div>
                    <DistCard title="Top skills" items={m.top_skills}/>
                </div>

                <aside className="space-y-4">
                    <div className="rounded-xl border border-white/8 bg-gradient-to-br from-[var(--jr-brand)]/10 to-transparent p-6">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">Fairness score</div>
                        <div className="mt-2 flex items-end gap-2">
                            <span className="font-display text-6xl tracking-tighter text-white">{data.fairness_score}</span>
                            <span className="text-[var(--jr-text-2)] mb-2">/ 100</span>
                        </div>
                        <p className="text-xs text-[var(--jr-text-2)] mt-3">Combined: data-kwaliteit + 4/5-regel-conformiteit.</p>
                    </div>
                    <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5">
                        <Scale className="text-[var(--jr-amber)]" size={20}/>
                        <h4 className="font-display text-base mt-3">EU AI Act conformiteit</h4>
                        <p className="text-xs text-[var(--jr-text-2)] mt-2 leading-relaxed">Recruitment-AI is &lsquo;limited risk&rsquo;. Orchestrator biedt transparantie, human-in-the-loop en bias-monitoring per workspace.</p>
                    </div>
                    <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5">
                        <h4 className="font-display text-base">Snel</h4>
                        <ul className="mt-3 space-y-1.5 text-xs text-[var(--jr-text-2)]">
                            <li>Totaal kandidaten: {m.total_candidates}</li>
                            <li>Totaal matches: {m.total_matches}</li>
                            <li>Onbekend gender %: {m.gender_distribution.u.pct}</li>
                        </ul>
                    </div>
                </aside>
            </div>
        </PageHeader>
    );
}

function Bar({ label, value }) {
    return (
        <div className="flex items-center gap-3">
            <span className="w-20 text-xs text-[var(--jr-text-2)]">{label}</span>
            <div className="flex-1 h-3 rounded bg-white/5 overflow-hidden">
                <div className="h-full bg-[var(--jr-brand)]" style={{ width: `${Math.min(100, value)}%` }} />
            </div>
            <span className="w-12 text-right text-xs text-white font-mono">{value}%</span>
        </div>
    );
}

function DistCard({ title, items }) {
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5">
            <h4 className="font-display text-base mb-3">{title}</h4>
            <div className="space-y-1.5">
                {(items || []).map((it) => <Bar key={it.label} label={it.label} value={it.pct}/>)}
                {(!items || items.length === 0) && <div className="text-sm text-[var(--jr-text-3)]">—</div>}
            </div>
        </div>
    );
}
